import { Component, ErrorInfo, ReactNode } from 'react';

type SimpleErrorBoundaryProps = {
	children: ReactNode;
	fallback: ReactNode;
	onError?: (error: Error, errorInfo: ErrorInfo) => void;
};

type SimpleErrorBoundaryState = {
	hasError: boolean;
};

/**
 * Simple error boundary that renders a fallback UI when an error occurs.
 * Use this for smaller components where a full error page isn't appropriate.
 */
export class SimpleErrorBoundary extends Component<SimpleErrorBoundaryProps, SimpleErrorBoundaryState> {
	constructor(props: SimpleErrorBoundaryProps) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError(): SimpleErrorBoundaryState {
		return { hasError: true };
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
		console.error('SimpleErrorBoundary caught error:', error, errorInfo);
		this.props.onError?.(error, errorInfo);
	}

	render(): ReactNode {
		if (this.state.hasError) {
			return this.props.fallback;
		}
		return this.props.children;
	}
}
